"use client";

import { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
function LabValuesConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const [searchQuery, setSearchQuery] = useState("");
  const filteredPosts = useMemo(() => {
    if (!searchQuery.trim()) return posts;
    const query = searchQuery.toLowerCase();
    return posts.filter(post => post.title.toLowerCase().includes(query) || post.customFields?.description?.toLowerCase().includes(query) || post.customFields?.normal_range?.toLowerCase().includes(query));
  }, [posts, searchQuery]);
  if (loading) {
    return <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="mb-12">
          <div className="mx-auto h-12 w-full max-w-2xl animate-pulse rounded-lg bg-muted/30" />
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-64 animate-pulse rounded-xl bg-muted/30" />)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-6 text-center">
          <p className="text-sm text-destructive">
            Failed to load lab values. Please try again later.
          </p>
        </div>
      </div>;
  }
  return <div className="mx-auto max-w-7xl px-6 py-16">
      <div className="mb-12">
        <div className="relative mx-auto max-w-2xl">
          <Search className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" />
          <Input type="text" placeholder="Search lab values..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="h-12 rounded-lg border-border pl-12 pr-4 shadow-[inset_0_2px_4px_rgb(0_0_0_/_0.06)] transition-transform duration-150 ease-out focus:scale-[1.02] focus:shadow-medium" />
        </div>
      </div>

      {filteredPosts.length === 0 ? <div className="rounded-xl bg-muted/20 p-12 text-center">
          <p className="text-muted-foreground">
            No lab values found matching your search.
          </p>
        </div> : <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredPosts.map((post, i) => <Card key={post.id} data-index={i} className="group border border-border bg-background shadow-soft transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-1 hover:shadow-medium">
              <CardHeader>
                <div className="mb-3 flex size-12 items-center justify-center rounded-lg bg-primary/10">
                  <svg className="size-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <CardTitle className="text-lg">{post.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {post.customFields?.normal_range && <div>
                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Normal Range
                    </p>
                    <p className="text-sm font-semibold text-foreground">
                      {post.customFields.normal_range}
                    </p>
                  </div>}
                {post.customFields?.description && <p className="line-clamp-3 text-sm text-muted-foreground">
                    {post.customFields.description}
                  </p>}
                {post.link && <Link to={post.link}>
                    <Button variant="outline" size="sm" className="w-full transition-all duration-[80ms] ease-[var(--ease-spring)] hover:scale-[0.98] hover:bg-accent/80">
                      Read More
                    </Button>
                  </Link>}
              </CardContent>
            </Card>)}
        </div>}
    </div>;
}
export default function LabValuesDirectory() {
  return <section className="bg-light-background">
      <PostsProvider postType="lab_value" wp_query={{
      per_page: 100,
      order: "asc",
      orderby: "title"
    }} embeds={[]} fields={["id", "title.rendered", "link", "meta"]}>
        <LabValuesConsumer />
      </PostsProvider>
    </section>;
}